import * as Yup from 'yup';
import { getEnv } from '../../utils/featureFlag';

const { REACT_APP_INVENTORY_MAX_MODELS_SELECTION: maxModelsAllowed } = getEnv();

const maxModelsLimitAllowed = maxModelsAllowed ?? 20;
const maxContentCharactersAllowed = 4500;
const otherTextContentCharactersAllowed = 1500;

const makeWithModelsSchema = Yup.object({
  makeId: Yup.number().nullable(),
  makeName: Yup.string().nullable(),
  models: Yup.array()
    .of(
      Yup.object({
        modelId: Yup.number().nullable(),
        modelName: Yup.string().nullable()
      })
    )
    .max(
      maxModelsLimitAllowed,
      `Number of selected vehicles is higher than allowed maximum of ${maxModelsLimitAllowed}. Please ensure that no more than ${maxModelsLimitAllowed} vehicles are selected.`
    )
    .nullable()
});

export const inventoryForm = Yup.object({
  contentType: Yup.string().required('Content Type is required').default('inventory'),
  year: Yup.number().required('Year is required'),
  inventoryType: Yup.string().default('new').oneOf(['new', 'used']),
  vehicles: Yup.array()
    .of(
      Yup.lazy((_, options) => {
        const index = options.index;
        if (index === 0) {
          return makeWithModelsSchema.required('The first Make field is required');
        } else {
          return makeWithModelsSchema.nullable();
        }
      })
    )
    .required('Makes are required')
    .test('no-null-elements', 'Vehicles cannot contain null elements', function (value) {
      const firstVehicle = value && value[0];
      if (!Array.isArray(value) || !firstVehicle?.makeId || !firstVehicle?.makeName) {
        return this.createError({ message: 'The first Make field is required' });
      }
      return true;
    }),
  filter: Yup.object({
    bodyStyles: Yup.array().of(Yup.string()),
    sizes: Yup.array().of(Yup.string()),
    fuelTypes: Yup.array().of(Yup.string())
  }),
  contentTopic: Yup.string()
    .required('Content Topic is required')
    .max(
      otherTextContentCharactersAllowed,
      `Content Topic must be less than ${otherTextContentCharactersAllowed} characters`
    )
    .min(5, 'Content Topic must be more than 5 characters')
    .trim(),
  wordCount: Yup.number()
    .required('Word count is required')
    .max(2500, 'Word count must be less than 2500')
    .min(100, 'Word count must be more than 100'),
  targetAudience: Yup.string()
    .optional()
    .max(
      otherTextContentCharactersAllowed,
      `Target Audience or Market must be less than ${otherTextContentCharactersAllowed} characters`
    ),
  geos: Yup.string()
    .optional()
    .max(
      otherTextContentCharactersAllowed,
      `Geos must be less than ${otherTextContentCharactersAllowed} characters`
    ),
  contentOne: Yup.object({
    content: Yup.string()
      .optional()
      .max(
        maxContentCharactersAllowed,
        `Content Example 1 must be less than ${maxContentCharactersAllowed} characters`
      ),
    writeStyle: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Content 1 Writing Style example must be less than ${otherTextContentCharactersAllowed} characters`
      )
  }).optional(),
  contentTwo: Yup.object({
    content: Yup.string()
      .optional()
      .max(
        maxContentCharactersAllowed,
        `Content Example 2 must be less than ${maxContentCharactersAllowed} characters`
      ),
    writeStyle: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Content 2 Writing Style example must be less than ${otherTextContentCharactersAllowed} characters`
      )
  }).optional(),
  additionalInformation: Yup.string()
    .optional()
    .max(
      otherTextContentCharactersAllowed,
      `Additional Information must be less than ${otherTextContentCharactersAllowed} characters`
    ),
  negativePrompt: Yup.string()
    .optional()
    .max(
      otherTextContentCharactersAllowed,
      `Negative Prompt must be less than ${otherTextContentCharactersAllowed} characters`
    ),
  linkOne: Yup.object({
    url: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Link 1 url must be less than ${otherTextContentCharactersAllowed} characters`
      ),
    description: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Link 1 description must be less than ${otherTextContentCharactersAllowed} characters`
      )
  }).optional(),
  linkTwo: Yup.object({
    url: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Link 2 url must be less than ${otherTextContentCharactersAllowed} characters`
      ),
    description: Yup.string()
      .optional()
      .max(
        otherTextContentCharactersAllowed,
        `Link 2 description must be less than ${otherTextContentCharactersAllowed} characters`
      )
  }).optional(),
  executeCopyscapeSearch: Yup.boolean().optional(),
  dealershipAccount: Yup.object({
    id: Yup.string().when(['name', 'address'], ([name, address], schema) => {
      return !name && !Object.keys(address ?? {}).length
        ? schema.max(0, 'Dealership account could not be found')
        : schema.required().max(50, 'Dealership ID must be less than 50 characters');
    }),
    name: Yup.string()
      .optional()
      .nullable(true)
      .max(100, 'Dealership name must be less than 100 characters'),
    address: Yup.object({
      address1: Yup.string()
        .optional()
        .nullable(true)
        .max(100, 'Address 1 must be less than 100 characters'),
      address2: Yup.string()
        .optional()
        .nullable(true)
        .max(100, 'Address 2 must be less than 100 characters'),
      address3: Yup.string()
        .optional()
        .nullable(true)
        .max(100, 'Address 3 must be less than 100 characters'),
      city: Yup.string().optional().nullable(true).max(50, 'City must be less than 50 characters'),
      state: Yup.string().optional().nullable(true).max(2, 'State must be less than 2 characters'),
      postalCode: Yup.string()
        .optional()
        .nullable(true)
        .max(10, 'Postal code must be less than 10 characters')
    })
      .optional()
      .default(undefined)
  }).optional()
});

export const initialValues = {
  inventoryType: 'new',
  vehicles: [],
  filter: {},
  contentTopic: '',
  wordCount: '',
  targetAudience: '',
  geos: '',
  contentOne: {
    content: '',
    writeStyle: ''
  },
  contentTwo: {
    content: '',
    writeStyle: ''
  },
  additionalInformation: '',
  negativePrompt: '',
  linkOne: {
    url: '',
    description: ''
  },
  linkTwo: {
    url: '',
    description: ''
  },
  executeCopyscapeSearch: true,
  dealershipAccount: {
    id: ''
  }
};

export const ASYNC_FIELDS = ['dealershipAccount'];
